import React, { ReactNode } from 'react';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import CircularProgress from '@mui/material/CircularProgress';

const iconsHub = {
  reset: {
    icon: <CachedOutlinedIcon sx={{ fontSize: 14, my: 'auto', mb: 0.4 }} />,
  },
  success: {
    icon: (
      <CheckCircleOutlineOutlinedIcon
        sx={{ fontSize: 14, my: 'auto', mb: 0.4 }}
      />
    ),
  },
  filter: {
    icon: <FilterListOutlinedIcon sx={{ fontSize: 14, my: 'auto', mb: 0.4 }} />,
  },
  grid: {
    icon: (
      <SpaceDashboardOutlinedIcon sx={{ fontSize: 14, my: 'auto', mb: 0.4 }} />
    ),
  },
  edit_filled: {
    icon: <EditIcon sx={{ fontSize: 14, my: 'auto', mb: 0.4 }} />,
  },
  download: {
    icon: (
      <FileDownloadOutlinedIcon sx={{ fontSize: 14, my: 'auto', mb: 0.4 }} />
    ),
  },
  info: {
    icon: <InfoOutlinedIcon sx={{ fontSize: 14, my: 'auto', mb: 0.4 }} />,
  },
  delete: {
    icon: <DeleteOutlinedIcon sx={{ fontSize: 14, my: 'auto', mb: 0.4 }} />,
  },
  loading: {
    icon: <CircularProgress sx={{ fontSize: 14, my: 'auto', mb: 0.4 }} />,
  },
};


type IconKey = 'reset' | 'success';

interface filterActionsProps {
  label: string;
  icon: IconKey;
  variant: 'text' | 'contained' | 'outlined';
  handleClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}

interface DrawerControlProps {
  title: string;
  subTitle: string;
  isOpen: boolean;
  handleClose: () => void;
  position?: 'right' | 'left';
  filterActions?: null | filterActionsProps[] | any;
  children: ReactNode;
  width?: string;
  headerChildren?: ReactNode;
}

export const DrawerControl: React.FC<DrawerControlProps> = ({
  title,
  subTitle,
  isOpen,
  handleClose,
  position = 'right',
  filterActions,
  children,
  width = '30%',
  headerChildren
}) => {
  return (
    <Drawer
      open={isOpen}
      anchor={position}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: '50%', md: '40%', lg: width },
          boxSizing: 'border-box',
        },
      }}
    >
      <Box
        sx={{
          height: '100vh',
          flexDirection: 'column',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        {
          headerChildren ? headerChildren :
            <Box
              sx={{
                padding: 2,
                display: 'flex',
                justifyContent: 'space-between',
                bgcolor: 'action.hover',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'start',
                }}
              >
                <Typography variant="subtitle1" color="text.primary">
                  {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {subTitle}
                </Typography>
              </Box>
              <IconButton onClick={handleClose}>
                <CloseOutlinedIcon sx={{ color: 'action.active' }} />
              </IconButton>
            </Box>
        }
        <Box
          sx={{
            height: '100%',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          {children}
        </Box>
        {
          filterActions &&
          <Stack
            sx={{
              padding: 2,
              bgcolor: 'action.hover',
            }}
            flexDirection={'row'}
            justifyContent={'flex-end'}
            alignItems={'center'}
            columnGap={2}
          >
            {
              filterActions.map((value: filterActionsProps) => (
                <Button
                  variant={value.variant}
                  onClick={value.handleClick}
                  key={value.label}
                  loadingPosition="start"
                  startIcon={iconsHub[value.icon]?.icon}
                  disabled={value.disabled || value.loading}
                  loading={value.loading}
                >
                  {value.label}
                </Button>
              ))
            }
          </Stack>
        }
      </Box>
    </Drawer>
  );
};